/**
 * PeopleCell Component
 *
 * Renders assigned people as avatars.
 * Clicking opens a dropdown to select from available workspace members.
 */

import { useState, Fragment } from 'react';
import { Combobox, Transition } from '@headlessui/react';
import { CheckIcon, XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import type { PeopleCellProps } from './types';

export function PeopleCell({
  value,
  onChange,
  availablePeople = [],
  multiple = true,
  readonly = false,
}: PeopleCellProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');

  const selectedIds = value?.personIds ?? [];

  // Filter people based on search query
  const filteredPeople = query === ''
    ? availablePeople
    : availablePeople.filter((person) =>
        person.name.toLowerCase().includes(query.toLowerCase()) ||
        person.email.toLowerCase().includes(query.toLowerCase())
      );

  // Get selected people details
  const selectedPeople = selectedIds
    .map(id => availablePeople.find(p => p.id === id))
    .filter(Boolean) as NonNullable<typeof availablePeople[0]>[];

  const handleSelect = (personId: string) => {
    if (readonly || !onChange) return;

    if (multiple) {
      const newIds = selectedIds.includes(personId)
        ? selectedIds.filter(id => id !== personId)
        : [...selectedIds, personId];
      onChange(newIds.length > 0 ? { personIds: newIds } : null);
    } else {
      onChange({ personIds: [personId] });
      setIsOpen(false);
    }
  };

  const handleRemove = (personId: string) => {
    if (readonly || !onChange) return;
    const newIds = selectedIds.filter(id => id !== personId);
    onChange(newIds.length > 0 ? { personIds: newIds } : null);
  };

  // Avatar component
  const Avatar = ({ person, size = 'sm' }: { person: typeof availablePeople[0]; size?: 'sm' | 'md' }) => {
    const sizeClasses = size === 'sm' ? 'h-6 w-6 text-2xs' : 'h-8 w-8 text-xs';

    if (person.avatarUrl) {
      return (
        <img
          src={person.avatarUrl}
          alt={person.name}
          className={`${sizeClasses} rounded-full object-cover`}
        />
      );
    }

    // Generate initials
    const initials = person.name
      .split(' ')
      .map(n => n[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();

    return (
      <div
        className={`${sizeClasses} rounded-full bg-primary-100 flex items-center justify-center border border-white`}
      >
        <span className="font-medium text-primary-600">{initials}</span>
      </div>
    );
  };

  if (readonly) {
    return (
      <div className="w-full h-full flex items-center">
        {selectedPeople.length > 0 ? (
          <div className="flex -space-x-1">
            {selectedPeople.slice(0, 3).map((person) => (
              <Avatar key={person.id} person={person} />
            ))}
            {selectedPeople.length > 3 && (
              <div className="h-6 w-6 rounded-full bg-gray-100 border border-white flex items-center justify-center">
                <span className="text-2xs font-medium text-gray-600">
                  +{selectedPeople.length - 3}
                </span>
              </div>
            )}
          </div>
        ) : (
          <span className="text-gray-400 text-sm">-</span>
        )}
      </div>
    );
  }

  // If no available people data, show simplified view
  if (availablePeople.length === 0) {
    return (
      <div className="w-full h-full flex items-center">
        {selectedIds.length > 0 ? (
          <div className="flex -space-x-1">
            {selectedIds.slice(0, 3).map((id, i) => (
              <div
                key={id}
                className="h-6 w-6 rounded-full bg-primary-100 border border-white flex items-center justify-center"
              >
                <span className="text-2xs font-medium text-primary-600">U{i + 1}</span>
              </div>
            ))}
            {selectedIds.length > 3 && (
              <div className="h-6 w-6 rounded-full bg-gray-100 border border-white flex items-center justify-center">
                <span className="text-2xs font-medium text-gray-600">
                  +{selectedIds.length - 3}
                </span>
              </div>
            )}
          </div>
        ) : (
          <span className="text-gray-400 text-sm">-</span>
        )}
      </div>
    );
  }

  return (
    <Combobox value={selectedIds} onChange={() => {}} multiple>
      <div className="relative w-full">
        <div
          className="w-full h-full flex items-center cursor-pointer min-h-[28px]"
          onClick={() => setIsOpen(true)}
        >
          {selectedPeople.length > 0 ? (
            <div className="flex -space-x-1">
              {selectedPeople.slice(0, 3).map((person) => (
                <Avatar key={person.id} person={person} />
              ))}
              {selectedPeople.length > 3 && (
                <div className="h-6 w-6 rounded-full bg-gray-100 border border-white flex items-center justify-center">
                  <span className="text-2xs font-medium text-gray-600">
                    +{selectedPeople.length - 3}
                  </span>
                </div>
              )}
            </div>
          ) : (
            <span className="text-gray-400 text-sm hover:text-gray-500">-</span>
          )}
        </div>

        <Transition
          show={isOpen}
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          afterLeave={() => {
            setIsOpen(false);
            setQuery('');
          }}
        >
          <div className="absolute z-50 mt-1 w-64 bg-white rounded-lg shadow-dropdown border border-surface-border overflow-hidden">
            {/* Search input */}
            <div className="p-2 border-b border-surface-border">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Combobox.Input
                  className="w-full pl-8 pr-3 py-1.5 text-sm border border-surface-border rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
                  placeholder="Search people..."
                  onChange={(e) => setQuery(e.target.value)}
                  autoFocus
                />
              </div>
            </div>

            {/* Selected people (removable) */}
            {selectedPeople.length > 0 && (
              <div className="p-2 border-b border-surface-border">
                <div className="flex flex-wrap gap-1">
                  {selectedPeople.map((person) => (
                    <span
                      key={person.id}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-primary-50 text-primary-700 rounded-full"
                    >
                      {person.name}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemove(person.id);
                        }}
                        className="hover:text-primary-900"
                      >
                        <XMarkIcon className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* People list */}
            <Combobox.Options static className="max-h-48 overflow-auto py-1">
              {filteredPeople.length === 0 ? (
                <div className="px-4 py-3 text-sm text-gray-500">
                  No people found
                </div>
              ) : (
                filteredPeople.map((person) => (
                  <Combobox.Option
                    key={person.id}
                    value={person.id}
                    onClick={() => handleSelect(person.id)}
                    className={({ active }) =>
                      `relative cursor-pointer select-none py-2 px-3 ${
                        active ? 'bg-surface-hover' : ''
                      }`
                    }
                  >
                    <div className="flex items-center gap-3">
                      <Avatar person={person} size="md" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {person.name}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          {person.email}
                        </div>
                      </div>
                      {selectedIds.includes(person.id) && (
                        <CheckIcon className="h-4 w-4 text-primary-500" />
                      )}
                    </div>
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>

            {/* Close button */}
            <div className="border-t border-surface-border p-2">
              <button
                onClick={() => setIsOpen(false)}
                className="w-full px-3 py-1.5 text-sm text-gray-600 hover:bg-surface-hover rounded"
              >
                Done
              </button>
            </div>
          </div>
        </Transition>
      </div>
    </Combobox>
  );
}
